import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Building2, 
  Zap, 
  Settings, 
  BarChart3, 
  Bell, 
  MapPin, 
  Thermometer, 
  Lightbulb, 
  Shield,
  Wifi,
  WifiOff,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Calendar,
  Users,
  Wrench,
  Eye,
  Plus,
  Filter,
  Search,
  Download,
  Upload,
  RefreshCw,
  Home,
  Droplets,
  Flame,
  Sun,
  Moon,
  Battery,
  Gauge,
  ChevronRight,
  ChevronDown,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronUp,
  Edit,
  Trash2,
  Move,
  Grid,
  List,
  Layers,
  Navigation,
  Target,
  Maximize2,
  Minimize2
} from 'lucide-react';
import SolarDeviceDashboard from '../../components/SolarDeviceDashboard';
import MeterDashboard from '../../components/MeterDashboard';
import { useTheme } from '../../contexts/ThemeContext';
import { masterSupabase } from '../../lib/supabase-master';

const ClaraIoTPage = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Hierarchie Tab specific states
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [viewMode, setViewMode] = useState('building'); // 'building', 'floor', 'unit'
  const [showDeviceAssignment, setShowDeviceAssignment] = useState(false);
  const [selectedUnitForDevices, setSelectedUnitForDevices] = useState(null);
  const [draggedDevice, setDraggedDevice] = useState(null);
  const [showUnitDetails, setShowUnitDetails] = useState(false);
  const [selectedUnitDetails, setSelectedUnitDetails] = useState(null);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitFormData, setUnitFormData] = useState({});
  const [assignedDevices, setAssignedDevices] = useState({});
  // Solar device discovery states
  const [solarDevices, setSolarDevices] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);

  // Solar API functions
  // Add test solar device to show dashboard
  const [connectedSolarDevices, setConnectedSolarDevices] = useState([
    {
      id: "test-solar-1",
      name: "PV Balkonanlage",
      ip: "192.168.178.100",
      port: 502,
      manufacturer: "Solarman",
      connectionKey: "test-connection",
      status: "online"
    }
  ]);
  const SOLAR_API_BASE = '/api/solar';

  const handleQuickScan = async () => {
    setIsScanning(true);
    setScanResults(null);
    
    try {
      const response = await fetch(`${SOLAR_API_BASE}/discover/quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setScanResults(data.devices);
        setSolarDevices(data.devices);
        console.log('🔍 Quick scan completed:', data.devices);
      } else {
        console.error('❌ Quick scan failed:', data.error);
      }
    } catch (error) {
      console.error('❌ Quick scan error:', error);
    } finally {
      setIsScanning(false);
    }
  };

  const handleFullScan = async () => {
    setIsScanning(true);
    setScanResults(null);
    
    try {
      const response = await fetch(`${SOLAR_API_BASE}/discover/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subnet: '192.168.1',
          startIp: 1,
          endIp: 254
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setScanResults(data.devices);
        setSolarDevices(data.devices);
        console.log('🌐 Full scan completed:', data.devices);
      } else {
        console.error('❌ Full scan failed:', data.error);
      }
    } catch (error) {
      console.error('❌ Full scan error:', error);
    } finally {
      setIsScanning(false);
    }
  };

  const connectToSolarDevice = async (device) => {
    try {
      const response = await fetch(`${SOLAR_API_BASE}/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ip: device.ip,
          port: device.port,
          deviceId: device.deviceId,
          name: device.name
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setConnectedSolarDevices(prev => [...prev, { ...device, connectionKey: data.connectionKey }]);
        console.log('✅ Connected to solar device:', device.name);
      } else {
        console.error('❌ Connection failed:', data.error);
      }
    } catch (error) {
      console.error('❌ Connection error:', error);
    }
  };

  // Load owners and properties from Supabase
  useEffect(() => {
    loadOwnersAndProperties();
  }, []);

  const loadOwnersAndProperties = async () => {
    try {
      setLoadingData(true);
      
      // Get current user
      const { data: { user } } = await masterSupabase.auth.getUser();
      if (!user) {
        console.error('No user logged in');
        return;
      }

      // Load owners for this user first
      const { data: ownersData, error: ownerError } = await masterSupabase
        .from('owners')
        .select('id')
        .eq('created_by', user.id);

      if (ownerError) {
        console.error('Error loading owners:', ownerError);
        return;
      }

      console.log('✅ Loaded owners:', ownersData);
      const ownerIds = ownersData?.map(o => o.id) || [];

      // Load properties for these owners
      const { data: propertiesData, error: propError } = await masterSupabase
        .from('properties')
        .select('*')
        .in('owner_id', ownerIds);

      if (propError) {
        console.error('Error loading properties:', propError);
        return;
      }

      console.log('✅ Loaded properties:', propertiesData);
      setProperties(propertiesData || []);

      // Auto-select first property if available
      if (propertiesData && propertiesData.length > 0) {
        setSelectedProperty(propertiesData[0]);
        // Load units for first property
        await loadUnitsForProperty(propertiesData[0].id);
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  // Load units for selected property
  const loadUnitsForProperty = async (propertyId) => {
    try {
      const { data: unitsData, error: unitsError } = await masterSupabase
        .from('units')
        .select('*')
        .eq('property_id', propertyId)
        .order('unit_identifier', { ascending: true });

      if (unitsError) {
        console.error('Error loading units:', unitsError);
        return;
      }

      console.log('✅ Loaded units:', unitsData);
      setUnits(unitsData || []);
    } catch (error) {
      console.error('Error loading units:', error);
    }
  };

  // When property selection changes, load its units
  useEffect(() => {
    if (selectedProperty?.id) {
      loadUnitsForProperty(selectedProperty.id);
    } else {
      setUnits([]);
    }
  }, [selectedProperty]);

  // Mock properties for fallback (will be replaced by real data)
  const mockProperties = [
    { 
      id: 1, 
      ownerId: 1,
      name: 'Wohnkomplex Mitte', 
      address: 'Hauptstraße 123, 10115 Berlin',
      units: 24,
      devices: 45, 
      status: 'online',
      floors: 5,
      lastUpdate: '2 Min',
      energyEfficiency: 'A+',
      monthlyConsumption: 12450,
      alerts: 1,
      buildingType: 'residential'
    },
    { 
      id: 2, 
      ownerId: 1,
      name: 'Bürogebäude Nord', 
      address: 'Geschäftsstraße 456, 10117 Berlin',
      units: 12,
      devices: 32, 
      status: 'online',
      floors: 12,
      lastUpdate: '5 Min',
      energyEfficiency: 'B+',
      monthlyConsumption: 18200,
      alerts: 0,
      buildingType: 'commercial'
    },
    { 
      id: 3, 
      ownerId: 1,
      name: 'Studentenwohnheim Süd', 
      address: 'Universitätsstraße 789, 10119 Berlin',
      units: 9,
      devices: 28, 
      status: 'maintenance',
      floors: 3,
      lastUpdate: '1 Std',
      energyEfficiency: 'C',
      monthlyConsumption: 8900,
      alerts: 3,
      buildingType: 'residential'
    }
  ];

  // Extended building data for hierarchy view
  const buildingDetails = {
    1: {
      id: 1,
      name: 'Wohnkomplex Mitte',
      floors: [
        {
          id: 1,
          name: 'Erdgeschoss',
          level: 0,
          units: [
            { id: 1, name: 'EG-01', type: 'apartment', size: '65m²', tenant: 'Familie Müller', devices: 8, status: 'occupied' },
            { id: 2, name: 'EG-02', type: 'apartment', size: '72m²', tenant: 'Herr Schmidt', devices: 6, status: 'occupied' },
            { id: 3, name: 'EG-03', type: 'commercial', size: '45m²', tenant: 'Café Berlin', devices: 12, status: 'occupied' },
            { id: 4, name: 'EG-04', type: 'storage', size: '15m²', tenant: 'Lager', devices: 2, status: 'utility' }
          ]
        },
        {
          id: 2,
          name: '1. Obergeschoss',
          level: 1,
          units: [
            { id: 5, name: '1OG-01', type: 'apartment', size: '68m²', tenant: 'Familie Weber', devices: 9, status: 'occupied' },
            { id: 6, name: '1OG-02', type: 'apartment', size: '68m²', tenant: 'Frau Klein', devices: 7, status: 'occupied' },
            { id: 7, name: '1OG-03', type: 'apartment', size: '75m²', tenant: 'Familie Bauer', devices: 10, status: 'occupied' },
            { id: 8, name: '1OG-04', type: 'apartment', size: '62m²', tenant: '', devices: 4, status: 'vacant' }
          ]
        },
        {
          id: 3,
          name: '2. Obergeschoss',
          level: 2,
          units: [
            { id: 9, name: '2OG-01', type: 'apartment', size: '68m²', tenant: 'Herr Fischer', devices: 8, status: 'occupied' },
            { id: 10, name: '2OG-02', type: 'apartment', size: '68m²', tenant: 'Familie Richter', devices: 9, status: 'occupied' },
            { id: 11, name: '2OG-03', type: 'apartment', size: '75m²', tenant: 'Frau Hoffmann', devices: 11, status: 'occupied' },
            { id: 12, name: '2OG-04', type: 'apartment', size: '62m²', tenant: 'Herr Wagner', devices: 6, status: 'occupied' }
          ]
        }
      ]
    }
  };

  const mockUnits = [
    { 
      id: 1, 
      propertyId: 1, 
      name: 'Wohnung 1A', 
      tenantName: 'Familie Müller',
      floor: 1,
      size: '65m²',
      devices: 8, 
      status: 'online',
      lastActivity: '2 Min',
      energyUsage: 245,
      temperature: 21.5
    },
    { 
      id: 2, 
      propertyId: 1, 
      name: 'Wohnung 1B', 
      tenantName: 'Herr Schmidt',
      floor: 1,
      size: '72m²',
      devices: 6, 
      status: 'online',
      lastActivity: '5 Min',
      energyUsage: 198,
      temperature: 22.1
    },
    { 
      id: 3, 
      propertyId: 1, 
      name: 'Wohnung 2A', 
      tenantName: 'Familie Weber',
      floor: 2,
      size: '68m²',
      devices: 9, 
      status: 'offline',
      lastActivity: '2 Std',
      energyUsage: 0,
      temperature: 19.8
    }
  ];

  const devices = [
    { 
      id: 1, 
      unitId: 1, 
      name: 'Thermostat Wohnzimmer', 
      type: 'thermostat',
      location: 'Wohnzimmer',
      status: 'online', 
      value: '21.5°C',
      lastSeen: '2 Min',
      battery: 85,
      isFavorite: true
    },
    { 
      id: 2, 
      unitId: 1, 
      name: 'Wasserzähler', 
      type: 'water_meter',
      location: 'Küche',
      status: 'online', 
      value: '1,245 L',
      lastSeen: '1 Min',
      battery: null,
      isFavorite: false
    },
    { 
      id: 3, 
      unitId: 1, 
      name: 'Stromzähler', 
      type: 'power_meter',
      location: 'Flur',
      status: 'online', 
      value: '2.4 kW',
      lastSeen: '30 Sek',
      battery: null,
      isFavorite: true
    },
    { 
      id: 4, 
      unitId: 2, 
      name: 'Rauchmelder', 
      type: 'smoke_detector',
      location: 'Schlafzimmer',
      status: 'online', 
      value: 'OK',
      lastSeen: '5 Min',
      battery: 92,
      isFavorite: false
    },
    { 
      id: 5, 
      unitId: 3, 
      name: 'Thermostat Büro', 
      type: 'thermostat',
      location: 'Büro',
      status: 'offline', 
      value: '19.8°C',
      lastSeen: '2 Std',
      battery: 23,
      isFavorite: false
    }
  ];

  // Available devices for assignment
  const availableDevices = [
    { id: 'new-1', name: 'Neuer Thermostat', type: 'thermostat', status: 'unassigned' }, 
    { id: 'new-2', name: 'Neuer Wasserzähler', type: 'water_meter', status: 'unassigned' }, 
    { id: 'new-3', name: 'Neuer Stromzähler', type: 'power_meter', status: 'unassigned' }, 
    { id: 'new-4', name: 'Neuer Rauchmelder', type: 'smoke_detector', status: 'unassigned' }, 
    { id: 'new-5', name: 'Neue Beleuchtung', type: 'lighting', status: 'unassigned' },
    { id: 'new-6', name: 'PV Balkonanlage', type: 'pvBalkonanlage', status: 'unassigned' }, 
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-emerald-500';
      case 'offline': return 'bg-red-500';
      case 'maintenance': return 'bg-amber-500';
      case 'occupied': return 'bg-emerald-500';
      case 'vacant': return 'bg-gray-400';
      case 'utility': return 'bg-blue-500';
      default: return 'bg-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return Wifi;
      case 'offline': return WifiOff;
      case 'maintenance': return AlertTriangle;
      default: return AlertCircle;
    }
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'thermostat': return Thermometer;
      case 'water_meter': return Droplets;
      case 'power_meter': return Zap;
      case 'smoke_detector': return Shield;
      case 'lighting': return Lightbulb;
      case 'pvBalkonanlage': return Sun;
      default: return Activity;
    }
  };

  const getUnitTypeIcon = (type) => {
    switch (type) {
      case 'apartment': return Home;
      case 'commercial': return Building2;
      case 'storage': return Grid;
      default: return Home;
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 2000);
  };

  const handleDeviceDrop = (unitId, deviceId) => {
    // Handle device assignment logic here
    console.log(`Assigning device ${deviceId} to unit ${unitId}`);
    setAssignedDevices(prev => ({
      ...prev,
      [unitId]: [...(prev[unitId] || []), deviceId]
    }));
    setDraggedDevice(null);
  };

  const handleUnitEdit = (unit) => {
    setEditingUnit(unit.id);
    setUnitFormData({
      name: unit.name,
      type: unit.type,
      size: unit.size,
      tenant: unit.tenant || '',
      status: unit.status
    });
  };

  const handleUnitSave = () => {
    // Save unit changes logic here
    console.log('Saving unit:', editingUnit, unitFormData);
    setEditingUnit(null);
    setUnitFormData({});
  };

  const handleUnitCancel = () => {
    setEditingUnit(null);
    setUnitFormData({});
  };

  const handleShowUnitDetails = (unit) => {
    setSelectedUnitDetails(unit);
    setShowUnitDetails(true);
  };

  const handleRemoveDeviceFromUnit = (unitId, deviceId) => {
    setAssignedDevices(prev => ({
      ...prev,
      [unitId]: (prev[unitId] || []).filter(id => id !== deviceId)
    }));
  };

  const renderBuildingView = () => {
    if (!selectedBuilding) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((building) => (
            <div
              key={building.id}
              onClick={() => setSelectedBuilding(building.id)}
              className={`p-6 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-200 hover:scale-105 ${
                theme === 'dark' 
                  ? 'border-gray-600 hover:border-emerald-500 hover:bg-gray-700/50' 
                  : 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <Building2 className={`h-8 w-8 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <div className={`w-3 h-3 rounded-full ${getStatusColor(building.status)}`}></div>
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {building.name}
              </h3>
              <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {building.address}
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Etagen:</span>
                  <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {building.floors}
                  </div>
                </div>
                <div>
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Einheiten:</span>
                  <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {building.units}
                  </div>
                </div>
                <div>
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Geräte:</span>
                  <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {building.devices}
                  </div>
                </div>
                <div>
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Effizienz:</span>
                  <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {building.energyEfficiency}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    const building = buildingDetails[selectedBuilding];
    if (!building) return null;

    return (
      <div className="space-y-6">
                {/* Building Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSelectedBuilding(null)}
              className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              <ChevronLeft className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>
            <div>
              <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {building.name}
              </h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {building.floors.length} Etagen • {building.floors.reduce((acc, floor) => acc + floor.units.length, 0)} Einheiten
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode(viewMode === 'building' ? 'floor' : 'building')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'building' 
                  ? theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'
                  : theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Layers className="h-4 w-4 mr-1 inline" />
              Etagen
            </button>
            <button
              onClick={() => setShowDeviceAssignment(!showDeviceAssignment)}
              className={`px-3 py-1 rounded text-sm ${
                showDeviceAssignment 
                  ? theme === 'dark' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-600'
                  : theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Target className="h-4 w-4 mr-1 inline" />
              Geräte zuweisen
            </button>
          </div>
        </div>

                {/* Floor Navigation */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          {building.floors.map((floor) => (
            <button
              key={floor.id}
              onClick={() => setSelectedFloor(selectedFloor === floor.id ? null : floor.id)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all duration-200 ${
                selectedFloor === floor.id
                  ? theme === 'dark' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700'
                  : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Layers className="h-4 w-4" />
                <span>{floor.name}</span>
                <span className={`text-xs px-2 py-1 rounded ${
                  selectedFloor === floor.id
                    ? 'bg-white/20'
                    : theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                }`}>
                  {floor.units.length}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Floor Content */}
        {selectedFloor ? (
          <div className="space-y-4">
            {building.floors
              .filter(floor => floor.id === selectedFloor)
              .map(floor => (
                <div key={floor.id} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {floor.name} - Grundriss
                    </h4>
                    <div className="flex items-center space-x-2">
                      <button className={`px-3 py-1 rounded text-sm ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        <Grid className="h-4 w-4 mr-1 inline" />
                        Raster
                      </button>
                      <button className={`px-3 py-1 rounded text-sm ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        <Maximize2 className="h-4 w-4 mr-1 inline" />
                        Vollbild
                      </button>
                    </div>
                  </div>
                  
                                    {/* Interactive Floor Plan */}
                  <div className={`relative p-8 rounded-lg border-2 border-dashed min-h-96 ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}>
                    <div className="grid grid-cols-2 gap-4 h-full">
                      {floor.units.map((unit, index) => {
                        const UnitIcon = getUnitTypeIcon(unit.type);
                        const isEditing = editingUnit === unit.id;
                        return (
                          <div
                            key={unit.id}
                            onClick={() => !isEditing && setSelectedUnitForDevices(unit.id)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (draggedDevice) {
                                handleDeviceDrop(unit.id, draggedDevice);
                              }
                            }}
                            className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:scale-105 ${
                              selectedUnitForDevices === unit.id
                                ? theme === 'dark' ? 'border-emerald-500 bg-emerald-900/20' : 'border-emerald-500 bg-emerald-50'
                                : theme === 'dark' ? 'border-gray-600 bg-gray-700 hover:border-gray-500' : 'border-gray-300 bg-white hover:border-gray-400'
                            }`}
                          >
                            {/* Unit Edit Form */}
                            {isEditing ? (
                              <div className="space-y-3">
                                <input
                                  type="text"
                                  value={unitFormData.name || ''}
                                  onChange={(e) => setUnitFormData(prev => ({ ...prev, name: e.target.value }))}
                                  className={`w-full px-2 py-1 text-sm rounded border ${
                                    theme === 'dark' 
                                      ? 'bg-gray-600 border-gray-500 text-white' 
                                      : 'bg-white border-gray-300 text-gray-900'
                                  }`}
                                  placeholder="Unit Name"
                                />
                                <input
                                  type="text"
                                  value={unitFormData.size || ''}
                                  onChange={(e) => setUnitFormData(prev => ({ ...prev, size: e.target.value }))}
                                  className={`w-full px-2 py-1 text-sm rounded border ${
                                    theme === 'dark' 
                                      ? 'bg-gray-600 border-gray-500 text-white' 
                                      : 'bg-white border-gray-300 text-gray-900'
                                  }`}
                                  placeholder="Size"
                                />
                                <input
                                  type="text"
                                  value={unitFormData.tenant || ''}
                                  onChange={(e) => setUnitFormData(prev => ({ ...prev, tenant: e.target.value }))}
                                  className={`w-full px-2 py-1 text-sm rounded border ${
                                    theme === 'dark' 
                                      ? 'bg-gray-600 border-gray-500 text-white' 
                                      : 'bg-white border-gray-300 text-gray-900'
                                  }`}
                                  placeholder="Tenant"
                                />
                                <select
                                  value={unitFormData.type || ''}
                                  onChange={(e) => setUnitFormData(prev => ({ ...prev, type: e.target.value }))}
                                  className={`w-full px-2 py-1 text-sm rounded border ${
                                    theme === 'dark' 
                                      ? 'bg-gray-600 border-gray-500 text-white' 
                                      : 'bg-white border-gray-300 text-gray-900'
                                  }`}
                                >
                                  <option value="apartment">Apartment</option>
                                  <option value="commercial">Commercial</option>
                                  <option value="storage">Storage</option>
                                </select>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={handleUnitSave}
                                    className="flex-1 px-2 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700"
                                  >
                                    <CheckCircle className="h-3 w-3 inline mr-1" />
                                    Save
                                  </button>
                                  <button
                                    onClick={handleUnitCancel}
                                    className="flex-1 px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                  >
                                    <XCircle className="h-3 w-3 inline mr-1" />
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center space-x-2">
                                    <UnitIcon className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                    <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                      {unit.name}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnitEdit(unit);
                                      }}
                                      className={`p-1 rounded hover:bg-gray-600 ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                      <Edit className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleShowUnitDetails(unit);
                                      }}
                                      className={`p-1 rounded hover:bg-gray-600 ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                      <Eye className="h-3 w-3" />
                                    </button>
                                    <div className={`w-2 h-2 rounded-full ${getStatusColor(unit.status)}`}></div>
                                  </div>
                                </div>
                                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  <div>{unit.size}</div>
                                  <div>{unit.tenant || 'Leer'}</div>
                                  <div>{unit.devices + (assignedDevices[unit.id]?.length || 0)} Geräte</div>
                                </div>
                                
                                {/* Assigned devices preview */}
                                {assignedDevices[unit.id] && assignedDevices[unit.id].length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {assignedDevices[unit.id].slice(0, 3).map((deviceId) => {
                                      const device = availableDevices.find(d => d.id === deviceId);
                                      if (!device) return null;
                                      const DeviceIcon = getDeviceIcon(device.type);
                                      return (
                                        <div
                                          key={deviceId}
                                          className={`flex items-center space-x-1 px-2 py-1 rounded text-xs ${
                                            theme === 'dark' ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                                          }`}
                                        >
                                          <DeviceIcon className="h-3 w-3" />
                                          <span>{device.name.split(' ')[1]}</span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemoveDeviceFromUnit(unit.id, deviceId);
                                            }}
                                            className="ml-1 hover:text-red-500"
                                          >
                                            <XCircle className="h-2 w-2" />
                                          </button>
                                        </div>
                                      );
                                    })}
                                    {assignedDevices[unit.id].length > 3 && (
                                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        +{assignedDevices[unit.id].length - 3} mehr
                                      </span>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                            
                            {/* Device assignment indicator */}
                            {showDeviceAssignment && !isEditing && (
                              <div className={`absolute inset-0 border-2 border-dashed rounded-lg flex items-center justify-center ${
                                theme === 'dark' ? 'border-emerald-500 bg-emerald-900/10' : 'border-emerald-500 bg-emerald-50/50'
                              }`}>
                                <Target className="h-6 w-6 text-emerald-500" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          /* All Floors Overview */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {building.floors.map((floor) => (
              <div
                key={floor.id}
                onClick={() => setSelectedFloor(floor.id)}
                className={`p-6 rounded-lg border cursor-pointer transition-all duration-200 hover:scale-105 ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' 
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <Layers className={`h-6 w-6 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <span className={`text-sm px-2 py-1 rounded ${theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    Level {floor.level}
                  </span>
                </div>
                <h4 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {floor.name}
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Einheiten:</span>
                    <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {floor.units.length}
                    </div>
                  </div>
                  <div>
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Belegt:</span>
                    <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {floor.units.filter(u => u.status === 'occupied').length}
                    </div>
                  </div>
                  <div>
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Geräte:</span>
                    <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {floor.units.reduce((acc, unit) => acc + unit.devices, 0)}
                    </div>
                  </div>
                  <div>
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Status:</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className={`text-xs ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Online</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Device Assignment Panel */}
        {showDeviceAssignment && (
          <div className={`p-6 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Verfügbare Geräte
              </h4>
              <button
                onClick={() => setShowDeviceAssignment(false)}
                className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <XCircle className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableDevices.map((device) => {
                const DeviceIcon = getDeviceIcon(device.type);
                return (
                  <div
                    key={device.id}
                    draggable
                    onDragStart={() => setDraggedDevice(device.id)}
                    onDragEnd={() => setDraggedDevice(null)}
                    className={`p-4 rounded-lg border cursor-move transition-all duration-200 hover:scale-105 ${
                      draggedDevice === device.id
                        ? theme === 'dark' ? 'border-emerald-500 bg-emerald-900/20' : 'border-emerald-500 bg-emerald-50'
                        : theme === 'dark' ? 'border-gray-600 bg-gray-700 hover:border-gray-500' : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <DeviceIcon className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      <div>
                        <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {device.name}
                        </div>
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          {device.type}
                        </div>
                      </div>
                      <Move className={`h-4 w-4 ml-auto ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={`mt-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-blue-900/20 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
              <div className="flex items-center space-x-2">
                <Navigation className="h-4 w-4 text-blue-500" />
                <span className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                  Ziehen Sie Geräte auf die gewünschten Einheiten, um sie zuzuweisen
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Activity },
    { id: 'hierarchy', name: 'Hierarchie', icon: Building2 },
    { id: 'devices', name: 'Geräte', icon: Zap },
    { id: 'automation', name: 'Automatisierung', icon: Settings },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 },
    { id: 'maintenance', name: 'Wartung', icon: Wrench },    { id: 'meters', name: 'Zähler', icon: Gauge },

    { id: 'solar', name: 'Solar', icon: Sun },
  ];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-3xl font-bold ${theme === "dark" ? "text-white" : "text-gray-900"} flex items-center`}>
              <Activity className="w-8 h-8 mr-3 text-emerald-600" />
              Clara IoT
            </h1>
            <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Intelligente Gebäudeverwaltung und IoT-Monitoring
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Property Selector */}
            {properties.length > 0 && (
              <select
                value={selectedProperty?.id || ''}
                onChange={(e) => {
                  const prop = properties.find(p => p.id === e.target.value);
                  setSelectedProperty(prop);
                }}
                className={`px-4 py-2 rounded-lg border ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-white'
                    : 'bg-white border-gray-200 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
              >
                <option value="">Immobilie wählen...</option>
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>
                    {prop.name || prop.address || 'Unbenannte Immobilie'}
                  </option>
                ))}
              </select>
            )}
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`p-2 rounded-lg transition-colors ${
                theme === 'dark' 
                  ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' 
                  : 'bg-white hover:bg-gray-50 text-gray-600'
              } border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}
            >
              <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <div className="flex items-center space-x-2">
              <Bell className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                3
              </span>
            </div>
          </div>
        </div>

                {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-8 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? theme === 'dark'
                      ? 'bg-emerald-600 text-white shadow-lg'
                      : 'bg-emerald-100 text-emerald-700 shadow-md'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
                        {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Aktive Geräte
                    </p>
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      183
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-100 rounded-full">
                    <Activity className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <TrendingUp className="h-4 w-4 text-emerald-500 mr-1" />
                  <span className="text-sm text-emerald-500">+12%</span>
                  <span className={`text-sm ml-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    vs. letzter Monat
                  </span>
                </div>
              </div>

              <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Energieverbrauch
                    </p>
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      1,245 kWh
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Zap className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <TrendingDown className="h-4 w-4 text-emerald-500 mr-1" />
                  <span className="text-sm text-emerald-500">-8%</span>
                  <span className={`text-sm ml-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    vs. letzter Monat
                  </span>
                </div>
              </div>

              <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Warnungen
                    </p>
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      4
                    </p>
                  </div>
                  <div className="p-3 bg-amber-100 rounded-full">
                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <span className="text-sm text-amber-500">2 kritisch</span>
                  <span className={`text-sm ml-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    • 2 niedrig
                  </span>
                </div>
              </div>

              <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Kosteneinsparung
                    </p>
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      €2,340
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-100 rounded-full">
                    <TrendingDown className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <TrendingUp className="h-4 w-4 text-emerald-500 mr-1" />
                  <span className="text-sm text-emerald-500">+23%</span>
                  <span className={`text-sm ml-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    diesen Monat
                  </span>
                </div>
              </div>
            </div>

            {/* Recent Activity & Alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="p-6">
                  <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Aktuelle Warnungen
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Hoher Energieverbrauch
                        </p>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Wohnkomplex Mitte • vor 5 Min
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Battery className="h-4 w-4 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Niedrige Batterie
                        </p>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Thermostat Büro • vor 15 Min
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Wifi className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Gerät wieder online
                        </p>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Wasserzähler Küche • vor 1 Std
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="p-6">
                  <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Top Geräte
                  </h3>
                  <div className="space-y-4">
                    {devices.slice(0, 3).map(device => {
                      const DeviceIcon = getDeviceIcon(device.type);
                      return (
                        <div key={device.id} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <DeviceIcon className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                            <div>
                              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {device.name}
                              </p>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                {device.location}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {device.value}
                            </p>
                            <div className={`w-2 h-2 rounded-full ${getStatusColor(device.status)}`}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hierarchie Tab - NEW IMPLEMENTATION */}
        {activeTab === 'hierarchy' && (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Gebäude-Hierarchie
                </h3>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <Search className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                    <input
                      type="text"
                      placeholder="Suchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`px-3 py-1 text-sm rounded border ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                  </div>
                  <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2">
                    <Plus className="w-4 h-4" />
                    <span>Neues Gebäude</span>
                  </button>
                </div>
              </div>
              
              {renderBuildingView()}
            </div>
          </div>
        )}

        {/* Geräte Tab */}
        {activeTab === 'devices' && (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Geräte-Management
              </h3>
              
              {/* Device Discovery Section */}
              <div className={`mb-6 p-4 rounded-lg border-2 border-dashed ${theme === 'dark' ? 'border-emerald-600 bg-emerald-900/20' : 'border-emerald-300 bg-emerald-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-700'}`}>
                    🔍 Hardware-Geräte entdecken
                  </h4>
                  {scanResults && (
                    <span className={`text-sm px-2 py-1 rounded ${theme === 'dark' ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-200 text-emerald-800'}`}>
                      ✅ {scanResults.length} Geräte gefunden
                    </span>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-3 mb-3">
                  <button 
                    onClick={handleQuickScan}
                    disabled={isScanning}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      isScanning 
                        ? 'opacity-50 cursor-not-allowed' 
                        : theme === 'dark' 
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}
                  >
                    {isScanning ? '🔄 Scanne...' : '⚡ Quick Scan'}
                  </button>
                  
                  <button 
                    onClick={handleFullScan}
                    disabled={isScanning}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      isScanning 
                        ? 'opacity-50 cursor-not-allowed' 
                        : theme === 'dark' 
                          ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {isScanning ? '🌐 Scanne Netzwerk...' : '🌐 Vollständiger Scan'}
                  </button>
                </div>
                
                {/* Scan Results */}
                {scanResults && scanResults.length > 0 && (
                  <div className="mt-4">
                    <h5 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Gefundene Solar-Geräte:
                    </h5>
                    <div className="space-y-2">
                      {scanResults.map((device, index) => (
                        <div key={index} className={`flex items-center justify-between p-3 rounded border ${
                          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-center space-x-3">
                            <Sun className="w-5 h-5 text-yellow-500" />
                            <div>
                              <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {device.name}
                              </div>
                              <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                {device.ip}:{device.port} • {device.manufacturer}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => connectToSolarDevice(device)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                          >
                            Verbinden
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {scanResults && scanResults.length === 0 && (
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    ❌ Keine Solar-Geräte gefunden. Stelle sicher, dass dein Wechselrichter im gleichen Netzwerk ist.
                  </div>
                )}
              </div>
              
              <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                </h3>
                <div className="flex items-center space-x-2">
                  <button className={`px-3 py-1 rounded text-sm ${theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'}`}>
                    Grid
                  </button>
                  <button className={`px-3 py-1 rounded text-sm ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    Liste
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {devices.map(device => (
                  <div key={device.id} className={`p-4 rounded-lg border transition-all duration-200 ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' 
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${
                          device.status === 'online' ? 'bg-emerald-500' :
                          device.status === 'offline' ? 'bg-red-500' : 'bg-amber-500'
                        }`} />
                        <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {device.name}
                        </span>
                      </div>
                      {device.isFavorite && (
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      )}
                    </div>
                    <div className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {device.location}
                    </div>
                    <div className={`text-lg font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {device.value}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                        {device.lastSeen}
                      </span>
                      {device.battery && (
                        <div className="flex items-center space-x-1">
                          <Battery className={`h-3 w-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                            {device.battery}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Automatisierung Tab */}
        {activeTab === 'automation' && (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Automatisierung & Regeln
                </h3>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Neue Regel</span>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <Thermometer className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Heizungssteuerung
                        </h4>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Automatische Temperaturregelung basierend auf Anwesenheit
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs">Aktiv</span>
                      <button className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
                        <Settings className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Trigger:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Bewegung erkannt</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Aktion:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Heizung auf 21°C</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Letzte Ausführung:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>vor 2 Std</div>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Lightbulb className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Beleuchtungsautomatik
                        </h4>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Licht ein/aus basierend auf Tageszeit und Anwesenheit
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Aktiv</span>
                      <button className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
                        <Settings className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Trigger:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Sonnenuntergang</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Aktion:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Licht einschalten</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Letzte Ausführung:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>gestern 18:45</div>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Energiespar-Modus
                        </h4>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Automatische Reduzierung bei hohem Verbrauch
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">Pausiert</span>
                      <button className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
                        <Settings className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Trigger:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Verbrauch &gt; 3kW</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Aktion:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Heizung reduzieren</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Letzte Ausführung:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>vor 3 Tagen</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Aktive Regeln</h4>
                    <Settings className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="text-2xl font-bold text-emerald-500">8</div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>2 pausiert</p>
                </div>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Heute ausgeführt</h4>
                    <Activity className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-2xl font-bold text-blue-500">24</div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Automatisierungen</p>
                </div>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Energieeinsparung</h4>
                    <TrendingDown className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="text-2xl font-bold text-emerald-500">15%</div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>durch Automatisierung</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Analytics & Berichte
                </h3>
                <div className="flex items-center space-x-2">
                  <select className={`px-3 py-1 rounded border text-sm ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}>
                    <option>Letzten 7 Tage</option>
                    <option>Letzten 30 Tage</option>
                    <option>Letzten 3 Monate</option>
                  </select>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Export</span>
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Effizienzsteigerung</h4>
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="text-2xl font-bold text-emerald-500">23.5%</div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>vs. Vorjahr</p>
                </div>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Kostenreduktion</h4>
                    <TrendingDown className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-2xl font-bold text-blue-500">18.2%</div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>vs. Vorjahr</p>
                </div>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>CO₂-Reduktion</h4>
                    <TrendingDown className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="text-2xl font-bold text-emerald-500">1.2t</div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>diesen Monat</p>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h4 className={`font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Energieverbrauch Trend
                </h4>
                <div className="space-y-3">
                  {['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun'].map((month, index) => {
                    const values = [2840, 2650, 2420, 2180, 1950, 1820];
                    return (
                      <div key={month} className="flex items-center justify-between">
                        <span className={`text-sm w-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{month}</span>
                        <div className="flex-1 mx-4 bg-gray-300 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-emerald-500 h-2 rounded-full"
                            style={{ width: `${(values[index] / 3000) * 100}%` }}
                          />
                        </div>
                        <span className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {values[index]} kWh
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Wartung Tab */}
        {activeTab === 'maintenance' && (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="p-6">
              <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                🔧 Wartung & Instandhaltung
              </h3>
              <p className={`mt-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                Wartungsfunktionen werden hier implementiert.
              </p>
            </div>
          </div>
        )}
        
        {/* Solar Tab */}
        {activeTab === 'solar' && (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  🌞 Solar Dashboard
                </h3>
                <div className="flex items-center space-x-2">
                  <div className={`px-3 py-1 rounded text-sm ${theme === 'dark' ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800'}`}>
                    {connectedSolarDevices.length} Geräte verbunden
                  </div>
                </div>
              </div>
              
              {connectedSolarDevices.length === 0 ? (
                <div className="text-center py-12">
                  <Sun className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                  <h4 className={`text-lg font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Keine Solar-Geräte verbunden
                  </h4>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Gehe zum Geräte-Tab und scanne nach verfügbaren PV-Anlagen.
                  </p>
                  <button
                    onClick={() => setActiveTab('devices')}
                    className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Zu Geräte-Tab
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {connectedSolarDevices.map((device, index) => (
                    <SolarDeviceDashboard 
                      key={device.connectionKey || index}
                      device={device}
                      theme={theme}
                      apiBase={SOLAR_API_BASE}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
         )
        }
        
        {/* Meters Tab */}
        {activeTab === 'meters' && (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <MeterDashboard 
              propertyId={selectedProperty?.id} 
              theme={theme}
            />
          </div>
        )}
        
        {/* Wartung Tab */}
        {activeTab === 'maintenance' && (
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Wartung & Service
                </h3>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Wartung planen</span>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Wrench className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Thermostat Wohnzimmer
                        </h4>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Batterie wechseln
                        </p>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs">Geplant</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Fällig:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>20.09.2025</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Dauer:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>15 min</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Techniker:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Max Müller</div>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <Wrench className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Wasserzähler Küche
                        </h4>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Kalibrierung erforderlich
                        </p>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">Überfällig</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Fällig:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>15.09.2025</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Dauer:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>45 min</div>
                    </div>
                    <div>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Techniker:</span>
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Anna Weber</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Offene Aufgaben</h4>
                    <Clock className="w-5 h-5 text-amber-500" />
                  </div>
                  <div className="text-2xl font-bold text-amber-500">12</div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>3 überfällig</p>
                </div>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Diese Woche</h4>
                    <Calendar className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-2xl font-bold text-blue-500">5</div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>geplante Wartungen</p>
                </div>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Durchschnitt</h4>
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="text-2xl font-bold text-emerald-500">32</div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Minuten pro Wartung</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Unit Details Modal */}
      {showUnitDetails && selectedUnitDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`max-w-2xl w-full mx-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {selectedUnitDetails.name} - Details
                </h3>
                <button
                  onClick={() => setShowUnitDetails(false)}
                  className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  <XCircle className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Einheit
                    </label>
                    <div className={`mt-1 text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {selectedUnitDetails.name}
                    </div>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Typ
                    </label>
                    <div className={`mt-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {selectedUnitDetails.type === 'apartment' ? 'Wohnung' : 
                       selectedUnitDetails.type === 'commercial' ? 'Gewerbe' : 'Lager'}
                    </div>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Größe
                    </label>
                    <div className={`mt-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {selectedUnitDetails.size}
                    </div>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Mieter
                    </label>
                    <div className={`mt-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {selectedUnitDetails.tenant || 'Leer'}
                    </div>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Status
                    </label>
                    <div className="mt-1 flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(selectedUnitDetails.status)}`}></div>
                      <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                        {selectedUnitDetails.status === 'occupied' ? 'Belegt' : 
                         selectedUnitDetails.status === 'vacant' ? 'Leer' : 'Wartung'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Installierte Geräte
                    </label>
                    <div className="mt-2 space-y-2">
                      {devices.filter(d => d.unitId === selectedUnitDetails.id).map(device => {
                        const DeviceIcon = getDeviceIcon(device.type);
                        return (
                          <div key={device.id} className={`flex items-center justify-between p-2 rounded ${
                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <div className="flex items-center space-x-2">
                              <DeviceIcon className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                              <span className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {device.name}
                              </span>
                            </div>
                            <div className={`w-2 h-2 rounded-full ${getStatusColor(device.status)}`}></div>
                          </div>
                        );
                      })}
                      
                      {/* Newly assigned devices */}
                      {assignedDevices[selectedUnitDetails.id] && assignedDevices[selectedUnitDetails.id].map(deviceId => {
                        const device = availableDevices.find(d => d.id === deviceId);
                        if (!device) return null;
                        const DeviceIcon = getDeviceIcon(device.type);
                        return (
                          <div key={deviceId} className={`flex items-center justify-between p-2 rounded border-2 border-dashed ${
                            theme === 'dark' ? 'bg-emerald-900/20 border-emerald-500' : 'bg-emerald-50 border-emerald-300'
                          }`}>
                            <div className="flex items-center space-x-2">
                              <DeviceIcon className="h-4 w-4 text-emerald-500" />
                              <span className={`text-sm ${theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'}`}>
                                {device.name} (Neu zugewiesen)
                              </span>
                            </div>
                            <button
                              onClick={() => handleRemoveDeviceFromUnit(selectedUnitDetails.id, deviceId)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div>
                    <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Aktionen
                    </label>
                    <div className="mt-2 space-y-2">
                      <button
                        onClick={() => {
                          handleUnitEdit(selectedUnitDetails);
                          setShowUnitDetails(false);
                        }}
                        className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Einheit bearbeiten</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUnitForDevices(selectedUnitDetails.id);
                          setShowDeviceAssignment(true);
                          setShowUnitDetails(false);
                        }}
                        className="w-full px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center space-x-2"
                      >
                        <Target className="w-4 h-4" />
                        <span>Geräte zuweisen</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Management Modal */}
      {showDeviceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`max-w-4xl w-full mx-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Geräte-Management
              </h3>
              
              {/* Device Discovery Section */}
              <div className={`mb-6 p-4 rounded-lg border-2 border-dashed ${theme === 'dark' ? 'border-emerald-600 bg-emerald-900/20' : 'border-emerald-300 bg-emerald-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-700'}`}>
                    🔍 Hardware-Geräte entdecken
                  </h4>
                  {scanResults && (
                    <span className={`text-sm px-2 py-1 rounded ${theme === 'dark' ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-200 text-emerald-800'}`}>
                      ✅ {scanResults.length} Geräte gefunden
                    </span>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-3 mb-3">
                  <button 
                    onClick={handleQuickScan}
                    disabled={isScanning}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      isScanning 
                        ? 'opacity-50 cursor-not-allowed' 
                        : theme === 'dark' 
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}
                  >
                    {isScanning ? '🔄 Scanne...' : '⚡ Quick Scan'}
                  </button>
                  
                  <button 
                    onClick={handleFullScan}
                    disabled={isScanning}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      isScanning 
                        ? 'opacity-50 cursor-not-allowed' 
                        : theme === 'dark' 
                          ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {isScanning ? '🌐 Scanne Netzwerk...' : '🌐 Vollständiger Scan'}
                  </button>
                </div>
                
                {/* Scan Results */}
                {scanResults && scanResults.length > 0 && (
                  <div className="mt-4">
                    <h5 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Gefundene Solar-Geräte:
                    </h5>
                    <div className="space-y-2">
                      {scanResults.map((device, index) => (
                        <div key={index} className={`flex items-center justify-between p-3 rounded border ${
                          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-center space-x-3">
                            <Sun className="w-5 h-5 text-yellow-500" />
                            <div>
                              <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {device.name}
                              </div>
                              <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                {device.ip}:{device.port} • {device.manufacturer}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => connectToSolarDevice(device)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                          >
                            Verbinden
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {scanResults && scanResults.length === 0 && (
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    ❌ Keine Solar-Geräte gefunden. Stelle sicher, dass dein Wechselrichter im gleichen Netzwerk ist.
                  </div>
                )}
              </div>
              
              <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                </h3>
                <button
                  onClick={() => setShowDeviceModal(false)}
                  className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  <XCircle className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Verfügbare Geräte
                  </h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {availableDevices.map((device) => {
                      const DeviceIcon = getDeviceIcon(device.type);
                      return (
                        <div
                          key={device.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:scale-105 ${
                            theme === 'dark' ? 'border-gray-600 bg-gray-700 hover:border-gray-500' : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <DeviceIcon className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                              <div>
                                <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                  {device.name}
                                </div>
                                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {device.type}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                device.status === 'unassigned' 
                                  ? 'bg-gray-100 text-gray-800' 
                                  : 'bg-emerald-100 text-emerald-800'
                              }`}>
                                {device.status === 'unassigned' ? 'Verfügbar' : 'Zugewiesen'}
                              </span>
                              <button className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
                                <Plus className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <h4 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Zugewiesene Geräte
                  </h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {Object.entries(assignedDevices).map(([unitId, deviceIds]) => {
                      const unit = buildingDetails[selectedBuilding]?.floors
                        .flatMap(f => f.units)
                        .find(u => u.id.toString() === unitId);
                      if (!unit || !deviceIds.length) return null;
                      
                      return (
                        <div key={unitId} className={`p-3 rounded-lg border ${
                          theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                        }`}>
                          <div className={`font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {unit.name}
                          </div>
                          <div className="space-y-1">
                            {deviceIds.map(deviceId => {
                              const device = availableDevices.find(d => d.id === deviceId);
                              if (!device) return null;
                              const DeviceIcon = getDeviceIcon(device.type);
                              return (
                                <div key={deviceId} className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <DeviceIcon className="h-4 w-4 text-emerald-500" />
                                    <span className={`text-sm ${theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'}`}>
                                      {device.name}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleRemoveDeviceFromUnit(unitId, deviceId)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};




export default ClaraIoTPage;

